import { useEffect } from "react";
import { useSelector } from "react-redux";

import "vanilla-cookieconsent/dist/cookieconsent.css";
import "./overrides.scss";
import * as CookieConsent from "vanilla-cookieconsent";
import type {
	ConsentModalLayout,
	ConsentModalPosition,
	PreferencesModalLayout,
	PreferencesModalPosition,
} from "vanilla-cookieconsent";
import { axiosInstance } from "store";
import { API_BASE_URL } from "config_environment";
import { TStore } from "models";

declare global {
	interface Window {
		dataLayer: Record<string, any>[];
	}
}

const pushToDataLayer = (event: any) => {
	const dataLayer = window.dataLayer || [];
	dataLayer.push({ event });
};

//** #1473: durable server-side record of consent, in addition to the client-side cc_cookie. */
const getConsentLogsUrl = () => {
	try {
		return `${new URL(API_BASE_URL).origin}/api/consent-logs`;
	} catch {
		return "/api/consent-logs";
	}
};

const logConsentToServer = (membershipId?: string | number | null) => {
	try {
		const { acceptedCategories, rejectedCategories } =
			CookieConsent.getUserPreferences();

		const payload: {
			domain: string;
			acceptedCategories: string[];
			rejectedCategories: string[];
			membershipId?: string | number;
		} = {
			domain: window.location.hostname,
			acceptedCategories,
			rejectedCategories,
		};

		if (membershipId) {
			payload.membershipId = membershipId;
		}

		axiosInstance()
			.post(getConsentLogsUrl(), payload)
			.catch((error: any) => {
				console.warn("Failed to log consent to server", error);
			});
	} catch (error) {
		console.warn("Failed to log consent to server", error);
	}
};

const updateConsent = (membershipId?: string | number | null) => {
	pushToDataLayer("consent_update");
	logConsentToServer(membershipId);
};

const initializeConsent = (membershipId?: string | number | null) => {
	updateConsent(membershipId);
	setTimeout(() => {
		pushToDataLayer("consent_initial_selection");
	}, 500);
};

const getConfig = ({
	language,
	privacyPolicyUrl,
	cookiePolicy,
	membershipId,
}: {
	language: string;
	privacyPolicyUrl?: string;
	cookiePolicy?: string;
	membershipId?: string | number | null;
}) => {
	const policyLinks = [
		privacyPolicyUrl
			? `<a href="${privacyPolicyUrl}">Privacy Policy</a>`
			: undefined,
		cookiePolicy
			? `<a href="${cookiePolicy}">Cookie Policy</a>`
			: undefined,
	].filter((link): link is string => Boolean(link));
	const policyLinksDescription =
		policyLinks.length > 0
			? ` For more info, see ${policyLinks.join(" or ")}.`
			: "";
	const chinesePolicyLinksDescription = privacyPolicyUrl
		? `有关更多信息，请参阅我们的<a href="${privacyPolicyUrl}">隐私政策</a>。`
		: "";

	const config = {
		onFirstConsent: () => initializeConsent(membershipId),
		onChange: () => updateConsent(membershipId),
		guiOptions: {
			consentModal: {
				layout: "bar inline" as ConsentModalLayout,
				position: "bottom" as ConsentModalPosition,
				// #1468: keep Accept All / Reject All visually equal (ICO dark-pattern guidance).
				equalWeightButtons: true,
				flipButtons: false,
			},
			preferencesModal: {
				layout: "box" as PreferencesModalLayout,
				position: "right" as PreferencesModalPosition,
				equalWeightButtons: true,
				flipButtons: false,
			},
		},
		categories: {
			necessary: {
				readOnly: true,
			},
		},
		language: {
			default: language,
			// autoDetect: 'browser',
			translations: {
				en: {
					consentModal: {
						title: "We Value Your Privacy",
						description: `We use essential cookies and local storage to keep you signed in, secure the Platform, and remember your cookie choices. We do not currently use analytics or advertising cookies.${policyLinksDescription}`,
						acceptAllBtn: "Accept All",
						acceptNecessaryBtn: "Reject All",
						showPreferencesBtn: "Customise Settings",
					},
					preferencesModal: {
						title: "Privacy Preference Center",
						acceptAllBtn: "Accept All Cookies",
						acceptNecessaryBtn: "Reject All",
						savePreferencesBtn: "Save Preferences",
						closeIconLabel: "Close modal",
						serviceCounterLabel: "Service|Services",
						sections: [
							{
								title: "Cookie Usage",
								description:
									"The Platform currently uses only essential cookies and local storage. These keep you signed in, secure your session, and remember your Cookie Banner choice.",
							},
							{
								title: 'Strictly Necessary Cookies <span class="pm__badge">Always Active</span>',
								description:
									"These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences or filling in forms.",
								linkedCategory: "necessary",
							},
						],
					},
				},
				cn: {
					consentModal: {
						title: "我们重视您的隐私",
						description: `我们仅使用网站运行所必需的 cookie 和本地存储，以保持登录状态、保护平台安全并记住您的选择。我们目前不使用分析或广告 cookie。${chinesePolicyLinksDescription}`,
						acceptAllBtn: "接受所有",
						acceptNecessaryBtn: "拒绝所有",
						showPreferencesBtn: "自定义设置",
					},
					preferencesModal: {
						title: "隐私偏好中心",
						acceptAllBtn: "接受所有 cookie",
						acceptNecessaryBtn: "拒绝所有",
						savePreferencesBtn: "保存偏好",
						closeIconLabel: "关闭模态窗口",
						serviceCounterLabel: "服务",
						sections: [
							{
								title: "Cookie 使用",
								description:
									"平台目前仅使用必要的 cookie 和本地存储，用于保持登录状态、保护会话安全并记住 Cookie 横幅选择。",
							},
							{
								title: '严格必要的 Cookie <span class="pm__badge">始终活动</span>',
								description:
									"这些 cookie 对于网站的功能是必不可少的，不能在我们的系统中关闭。它们通常只是对您的操作做出的响应，这些操作相当于请求服务，例如设置您的隐私偏好或填写表单。",
								linkedCategory: "necessary",
							},
						],
					},
				},
			},
		},
	};

	return config;
};

export const CookieBanner = ({
	language = "en",
	privacyPolicyUrl,
	cookiePolicy,
}: {
	language?: string;
	privacyPolicyUrl?: string;
	cookiePolicy?: string;
}) => {
	// Only available once the user is logged in; consent logging works for anonymous
	// visitors too, membershipId is simply omitted from the payload in that case.
	const membershipId = useSelector(
		(state: TStore) => state.auth?.credentials?.user?.membership_id,
	);

	useEffect(() => {
		CookieConsent.setLanguage(language);
		CookieConsent.run(
			getConfig({
				language,
				privacyPolicyUrl,
				cookiePolicy,
				membershipId,
			}),
		);
	}, [language, privacyPolicyUrl, cookiePolicy, membershipId]);

	return null;
};
